import { ColDef } from 'ag-grid-community'
import { dateOnlyFormatter, dateTimeFormatter, userFormatter } from '../shared/utilities/formatters'


// subset of the entity types that we care about... can add more as needed
export const entityTypeCodes = [
  'ARRZ',
  'AO',
  'CPN',
  'DC',
  'MP',
  'RTLR',
  'CA',
  'MDMA',
  'OPCO',
  'SC',
  'SG',
  'TBILLACT',
  'TBILLAGENT',
] as const

export type EntityTypeCode = typeof entityTypeCodes[number]

export const defaultEntityTypeCode: EntityTypeCode = 'CPN'

export type EntityType = Readonly<{
  entity_type_code: EntityTypeCode
  entity_type_name: string
}>

export type EffectiveDateProperties = Readonly<{
  effective_date: string
  termination_date: string
}>

export type AuditProperties = Readonly<{
  create_time: string
  created_by: string
  last_update_time: string
  last_updated_by: string
}>

export type EntityBase = Readonly<{
  entity_uid: string
  entity_type_code: EntityTypeCode
  entity_name: string
  entity_code: string
}>

export type Entity = EntityBase & EffectiveDateProperties & Readonly<{
  effective_date: string | null
  termination_date: string | null
  entity_detail_value: string | null
  entity_business_type: string | null
  asset_name: string | null
  miso_id: string | null
  oasis_number: string | null
  mdma_weather_sensitive_flag: boolean | null
  min_limit: number | null
  max_limit: number | null
}>

export type ChildEntityCandidate = EntityBase & Readonly<{ termination_date: string | null }>

export type EntityDetailsProperties = EffectiveDateProperties & Readonly<{
  entity_detail_value?: string
  asset_name?: string
  entity_business_type?: string
  max_limit?: number
  min_limit?: number
  oasis_number?: string
  mdma_weather_sensitive_flag?: boolean
  miso_id?: string
}>

export type EntityDetails = EntityDetailsProperties & AuditProperties & Readonly<{
  entity_uid: string
}>

export const stringEntityDetailsFields = [
  'entity_detail_value',
  'asset_name',
  'entity_business_type',
  'oasis_number',
  'miso_id',
] as const

export type StringEntityDetailsField = typeof stringEntityDetailsFields[number]

export const numberEntityDetailsFields = [
  'min_limit',
  'max_limit',
] as const

export type NumberEntityDetailsField = typeof numberEntityDetailsFields[number]

export const booleanEntityDetailsFields = [
  'mdma_weather_sensitive_flag',
] as const

export type BooleanEntityDetailsField = typeof booleanEntityDetailsFields[number]

export const dateEntityDetailsFields = [
  'effective_date',
  'termination_date',
] as const

export type DateEntityDetailsField = typeof dateEntityDetailsFields[number]

export type EntityDetailsField = StringEntityDetailsField | NumberEntityDetailsField |
  BooleanEntityDetailsField | DateEntityDetailsField

type LabelLookup = Readonly<Partial<Record<EntityDetailsField, string>>>

const detailsFieldLabels: Readonly<Record<EntityDetailsField, string>> = {
  effective_date: 'Effective Date',
  termination_date: 'End Date',
  entity_detail_value: 'Value',
  asset_name: 'Asset Name',
  entity_business_type: 'Business Type',
  oasis_number: 'Oasis #',
  miso_id: 'MISO ID',
  min_limit: 'Min Limit',
  max_limit: 'Max Limit',
  mdma_weather_sensitive_flag: 'MDMA Weather Sensitive',
}

type LookupMap<T> = Record<string, T>

const createLookupMap = <T extends Record<string, unknown>> (
  partial: Partial<Record<EntityTypeCode, T>>,
): Readonly<LookupMap<T>> => {
  const result: LookupMap<T> = {}
  entityTypeCodes.forEach(typeCode => {
    result[typeCode] = partial[typeCode] ?? {} as T
  })
  return result as Readonly<Record<EntityTypeCode, T>>
}

const detailsFieldLabelOverrides: Readonly<LookupMap<LabelLookup>> = createLookupMap({
  CPN: {
    entity_business_type: 'Node Type',
  },
  TBILLACT: {
    entity_business_type: 'Account Type',
  },
  RTLR: {
    entity_business_type: 'Retailer Type',
  },
})

const lookupLabel = (lookup: LabelLookup, field: string): string | undefined =>
  lookup[field as EntityDetailsField]

const getDetailsFieldLabelOverride = (
  field: string,
  typeCode?: EntityTypeCode,
): string | undefined => typeCode && lookupLabel(detailsFieldLabelOverrides[typeCode], field)

export const getDetailsFieldLabel = (
  field: string,
  typeCode?: EntityTypeCode,
): string =>
  getDetailsFieldLabelOverride(field, typeCode) ?? lookupLabel(detailsFieldLabels, field) ?? field

export type EntityTypeProperties = Readonly<{
  required: readonly EntityDetailsField[]
  optional: readonly EntityDetailsField[]
}>

export type EntityTypeMetadata = EntityType & EntityTypeProperties & Readonly<{ editable: boolean }>

type EntityTypeRecord = Record<EntityTypeCode, EntityTypeMetadata>

export type EntityTypeMap = Readonly<EntityTypeRecord>

export type EntityRelationship = Readonly<{
  entity_relationship_uid: string
  entity_relationship_type_code: string
  child_entity_uid: string
  child_entity_code: string
  child_entity_name: string
  parent_entity_uid: string
  parent_entity_code: string
  parent_entity_name: string
}> & EffectiveDateProperties & AuditProperties

export type EntityRelationshipMap = Partial<Record<EntityTypeCode, EntityRelationship[]>>

export type RelationshipType = 'parent' | 'child'

export type EntityRelationshipType = Readonly<{
  child_entity_type_code: EntityTypeCode
  parent_entity_type_code: EntityTypeCode
}>

export type RelatedEntityValues = Readonly<{
  related_entity_uid: string
  effective_date: string
  termination_date: string
}>

export type ParentRelationshipArgs = Readonly<{
  entity_relationship_uid?: string
  values: RelatedEntityValues
}>

export const formatEntity = ({ entity_code, entity_name }: Partial<EntityBase>): string =>
  entity_name === entity_code ? entity_name! : `${entity_name} (${entity_code})`

export const entityDetailValueLists: Readonly<LookupMap<Record<string, readonly string[]>>> = createLookupMap({
  CPN: {
    entity_business_type: ['LOAD', 'GEN'],
  },
  TBILLACT: {
    entity_business_type: ['NL', 'RETAIL', 'WHOLESALE'],
  },
  RTLR: {
    entity_business_type: ['R', 'W'],
  },
})

export const effectiveDateFields = ['effective_date', 'termination_date'] as const

export type EffectiveDateField = typeof effectiveDateFields[number]

const createEffectiveDateColumn = (field: EffectiveDateField): ColDef => ({
  field,
  headerName: getDetailsFieldLabel(field),
  width: 150,
  editable: true,
  valueFormatter: dateOnlyFormatter,
  type: 'date',
})

export const effectiveDateColumns: Readonly<Record<EffectiveDateField, ColDef>> = {
  effective_date: createEffectiveDateColumn('effective_date'),
  termination_date: createEffectiveDateColumn('termination_date'),
}

export const auditColumnFields = [
  'last_updated_by',
  'last_update_time',
  'created_by',
  'create_time',
] as const

export type AuditColumnField = typeof auditColumnFields[number]

export const auditColumns: Readonly<Record<AuditColumnField, ColDef>> = {
  last_updated_by: {
    field: 'last_update_by',
    headerName: 'Updated By',
    valueFormatter: userFormatter,
  },
  last_update_time: {
    field: 'last_update_time',
    headerName: 'Updated',
    width: 220,
    valueFormatter: dateTimeFormatter,
  },
  created_by: {
    field: 'created_by',
    headerName: 'Created By',
    valueFormatter: userFormatter,
  },
  create_time: {
    field: 'create_time',
    headerName: 'Created',
    width: 220,
    valueFormatter: dateTimeFormatter,
  },
}

export type EditableRowField<T> = string & keyof T

export type ValidationMessageTarget = Readonly<{
  id: string
  field?: string
}>

export type ValidationMessage = Readonly<{
  text: string
  targets: readonly ValidationMessageTarget[]
}>

export type EditableRow<T> = Partial<T> & Readonly<{
  original?: T
  new?: boolean
}>
